(function() {
  if (window.JomoCartDeliveryZoneLoaded) return;
  window.JomoCartDeliveryZoneLoaded = true;

  const IP_LOCATION_CACHE_KEY = 'jomo-delivery-ip-location-v2';
  const SELECTED_ZONE_CACHE_KEY = 'jomo-delivery-selected-zone-v1';
  const SELECTION_MAX_AGE = 12 * 60 * 60 * 1000;
  const initializedBars = new WeakSet();
  const initializedTitles = new WeakSet();
  const zoneRequests = new Map();
  let locationRequest;

  function loadJson(url) {
    if (!zoneRequests.has(url)) {
      zoneRequests.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Delivery zone data returned ${response.status}`);
        return response.json();
      }));
    }

    return zoneRequests.get(url);
  }

  function getApproximateLocation(url) {
    if (locationRequest) return locationRequest;

    locationRequest = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(IP_LOCATION_CACHE_KEY));
        if (cached?.savedAt && Date.now() - cached.savedAt < 12 * 60 * 60 * 1000) return cached.location;
      } catch (_error) {
        // Storage can be unavailable in privacy modes; continue with one request.
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 4000);

      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Approximate location service returned ${response.status}`);

        const data = await response.json();
        const latitude = Number(data.location?.latitude ?? data.latitude);
        const longitude = Number(data.location?.longitude ?? data.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

        const location = {
          country: String(data.country || data.country_code || '').toUpperCase(),
          latitude,
          longitude
        };

        try {
          sessionStorage.setItem(IP_LOCATION_CACHE_KEY, JSON.stringify({ location, savedAt: Date.now() }));
        } catch (_error) {
          // The cart message still works without caching.
        }

        return location;
      } finally {
        clearTimeout(timeout);
      }
    })();

    return locationRequest;
  }

  function getSelectedDeliveryZone() {
    try {
      const selection = JSON.parse(sessionStorage.getItem(SELECTED_ZONE_CACHE_KEY));
      if (selection?.savedAt && Date.now() - selection.savedAt < SELECTION_MAX_AGE) return selection;
      sessionStorage.removeItem(SELECTED_ZONE_CACHE_KEY);
    } catch (_error) {
      // Fall back to approximate IP detection when storage is unavailable.
    }

    return null;
  }

  function pointOnSegment(point, start, end) {
    const [x, y] = point;
    const [x1, y1] = start;
    const [x2, y2] = end;
    const crossProduct = (y - y1) * (x2 - x1) - (x - x1) * (y2 - y1);
    if (Math.abs(crossProduct) > 1e-10) return false;

    return x >= Math.min(x1, x2) - 1e-10
      && x <= Math.max(x1, x2) + 1e-10
      && y >= Math.min(y1, y2) - 1e-10
      && y <= Math.max(y1, y2) + 1e-10;
  }

  function ringContainsPoint(ring, point) {
    let inside = false;
    const [x, y] = point;

    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
      const [xi, yi] = ring[index];
      const [xj, yj] = ring[previous];

      if (pointOnSegment(point, ring[previous], ring[index])) return true;

      const intersects = yi > y !== yj > y
        && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersects) inside = !inside;
    }

    return inside;
  }

  function polygonContainsPoint(rings, point) {
    if (!rings.length || !ringContainsPoint(rings[0], point)) return false;
    return !rings.slice(1).some((ring) => ringContainsPoint(ring, point));
  }

  function featureContainsPoint(feature, point) {
    if (feature.geometry?.type === 'Polygon') {
      return polygonContainsPoint(feature.geometry.coordinates, point);
    }

    if (feature.geometry?.type === 'MultiPolygon') {
      return feature.geometry.coordinates.some((polygon) => polygonContainsPoint(polygon, point));
    }

    return false;
  }

  function showLocalDelivery(bar, zoneName) {
    const isCore = zoneName === 'Toronto Core';
    const configuredThreshold = Number(isCore ? bar.dataset.coreThreshold : bar.dataset.gtaThreshold);
    const threshold = Number.isFinite(configuredThreshold) && configuredThreshold > 0
      ? configuredThreshold
      : (isCore ? 75 : 125);
    const area = isCore ? 'Toronto' : 'the GTA';

    bar.dataset.deliveryMode = 'local';
    bar.setAttribute('unreached-message', `You're {{ remaining_amount }} away from free delivery in ${area}.`);
    bar.setAttribute('reached-message', `Free delivery unlocked in ${area}.`);
    bar.removeAttribute('static-message');
    bar.setDeliveryThreshold(threshold * 100);
  }

  function showParcelShipping(bar) {
    bar.dataset.deliveryMode = 'parcel';
    bar.setAttribute('static-message', 'Canada wide shipping is available for plants and planters 7 inches or smaller.');
  }

  function applySelectedDeliveryZone(bar, selection) {
    if (selection?.service === 'local' && selection.zoneName) {
      showLocalDelivery(bar, selection.zoneName);
    } else {
      showParcelShipping(bar);
    }
  }

  function formatThreshold(value, fallback) {
    const amount = Number(value);
    const resolvedAmount = Number.isFinite(amount) && amount > 0 ? amount : fallback;
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'CAD',
      maximumFractionDigits: 0
    }).format(resolvedAmount);
  }

  function applySelectedDeliveryTitle(title, selection) {
    if (selection?.service === 'local' && selection.zoneName) {
      const isCore = selection.zoneName === 'Toronto Core';
      const threshold = formatThreshold(
        isCore ? title.dataset.coreThreshold : title.dataset.gtaThreshold,
        isCore ? 75 : 125
      );
      const area = isCore ? 'Toronto' : selection.zoneName;
      title.textContent = `Free delivery over ${threshold} in ${area}`;
      return;
    }

    if (selection?.service === 'shipping' && selection.provinceName) {
      title.textContent = `Shipping available to ${selection.provinceName}`;
      return;
    }

    if (selection?.service === 'territory') {
      title.textContent = 'Shipping available within Canada’s provinces';
      return;
    }

    if (selection?.service === 'outside') {
      title.textContent = 'Delivery and shipping within Canada';
      return;
    }

    title.textContent = 'Delivery and shipping';
  }

  async function detectDeliverySelection(element) {
    const [zones, canadaZones, location] = await Promise.all([
      loadJson(element.dataset.zonesUrl),
      loadJson(element.dataset.canadaZonesUrl),
      getApproximateLocation(element.dataset.ipGeocoderUrl)
    ]);

    if (!location || location.country !== 'CA') return { service: 'outside' };

    const point = [location.longitude, location.latitude];
    const localZone = zones.features.find((feature) => featureContainsPoint(feature, point));
    if (localZone) return { service: 'local', zoneName: localZone.properties.name };

    const province = canadaZones.features.find((feature) => featureContainsPoint(feature, point));
    if (!province) return { service: 'outside' };

    return province.properties.shippable
      ? { service: 'shipping', provinceName: province.properties.name }
      : { service: 'territory', provinceName: province.properties.name };
  }

  async function initializeTitle(title) {
    if (initializedTitles.has(title)) return;
    initializedTitles.add(title);

    const selectedZone = getSelectedDeliveryZone();
    if (selectedZone) {
      applySelectedDeliveryTitle(title, selectedZone);
      return;
    }

    try {
      applySelectedDeliveryTitle(title, await detectDeliverySelection(title));
    } catch (error) {
      console.info('[Delivery accordion] Using the general delivery title because approximate location was unavailable.', error);
      applySelectedDeliveryTitle(title, null);
    }
  }

  async function initializeBar(bar) {
    if (initializedBars.has(bar)) return;
    initializedBars.add(bar);

    await customElements.whenDefined('free-shipping-bar');

    const selectedZone = getSelectedDeliveryZone();
    if (selectedZone) {
      applySelectedDeliveryZone(bar, selectedZone);
      return;
    }

    try {
      const [zones, location] = await Promise.all([
        loadJson(bar.dataset.zonesUrl),
        getApproximateLocation(bar.dataset.ipGeocoderUrl)
      ]);

      if (!location || location.country !== 'CA') {
        showParcelShipping(bar);
        return;
      }

      const point = [location.longitude, location.latitude];
      const zone = zones.features.find((feature) => featureContainsPoint(feature, point));

      if (zone) {
        showLocalDelivery(bar, zone.properties.name);
      } else {
        showParcelShipping(bar);
      }
    } catch (error) {
      console.info('[Cart delivery zone] Using Canada wide shipping message because approximate location was unavailable.', error);
      showParcelShipping(bar);
    }
  }

  function initializeBars(root = document) {
    root.querySelectorAll?.('free-shipping-bar[data-zone-aware-delivery]').forEach((bar) => initializeBar(bar));
  }

  function initializeTitles(root = document) {
    root.querySelectorAll?.('[data-zone-aware-delivery-title]').forEach((title) => initializeTitle(title));
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('free-shipping-bar[data-zone-aware-delivery]')) initializeBar(node);
      if (node.matches('[data-zone-aware-delivery-title]')) initializeTitle(node);
      initializeBars(node);
      initializeTitles(node);
    }));
  });

  initializeBars();
  initializeTitles();
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('jomo:delivery-zone-change', async (event) => {
    document.querySelectorAll('[data-zone-aware-delivery-title]').forEach((title) => {
      applySelectedDeliveryTitle(title, event.detail);
    });

    await customElements.whenDefined('free-shipping-bar');
    document.querySelectorAll('free-shipping-bar[data-zone-aware-delivery]').forEach((bar) => {
      applySelectedDeliveryZone(bar, event.detail);
    });
  });
})();